import { Box, Button, Checkbox, Grid } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BasicDialog from "../../../components/base/BasicDialog";
import { InvestmentFilter } from "../../../components/common/InvestmentFilter";
import { ProductTypeFilter } from "../../../components/common/ProductTypeFilter";
import { FilterTableWrapper } from "../../../components/table/BetterFilterTable/BetterFilterTable";
import {
    NUMERIC_RANGE_FIELD_TYPE,
    SELECT_FIELD_TYPE,
    TEXT_FIELD_TYPE,
    AUTOCOMPLETE_FIELD_TYPE
} from "../../../helpers/constants";
import usePaginationWithLocalSearchParamsAndOrdering from "../../../hooks/usePaginationWithLocalSearchParamsAndOrdering";
import {
    useAdjacentProducts,
    useProductStatuses,
    useProductTypes,
} from "../../../services/productsService";
import { useLocalizedProperty } from "../../../utils";
import { ProductStatusText } from "../../ProductsPage/ProductStatusText";

export const RelatedProductsAddDialog = (props) => {
    const {
        onClose,
        onProductsSelected,
        excludedProductId,
        initiallySelectedProductIds = [],
    } = props;
    const { t } = useTranslation();

    const [tableDataLocal, setTableDataLocal] = useState([]);

    const pagination = usePaginationWithLocalSearchParamsAndOrdering();

    const selectedProductIdxs = tableDataLocal // @ts-ignore
        .filter((row) => row.selected)
        .map((row) => row.id);

    const areNoProductsSelected = selectedProductIdxs.length === 0;

    const handleConfirm = () => {
        onProductsSelected(selectedProductIdxs);
        onClose();
    };

    const setSearchParamsCallback = useCallback(
        (params) => {
            params.set("page", "1");
            pagination.setSearchParams(params);
            pagination.resetPageNumber();
        },
        [pagination.setSearchParams, pagination.resetPageNumber],
    );

    return (
        <BasicDialog
            open={true}
            onClose={onClose}
            titleAlign={"center"}
            contentAlign={"center"}
            title={t("products.RelatedProductsAddDialog.title")}
            maxWidth={"md"}
            showDialogActions
            alwaysFullScreen={false}
            contentSx={{ pt: 0 }}
        >
            <Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        padding: "1 rem",
                        gap: "1rem",
                        margin: "1rem 0",
                    }}
                >
                    <InvestmentFilter
                        searchParams={pagination.searchParams}
                        setSearchParams={setSearchParamsCallback}
                    />
                    <ProductTypeFilter
                        searchParams={pagination.searchParams}
                        setSearchParams={setSearchParamsCallback}
                    />
                </Box>

                <ProductsTable
                    pagination={pagination}
                    dataLocal={tableDataLocal}
                    setDataLocal={setTableDataLocal}
                    excludedProductId={excludedProductId}
                    initiallySelectedProductIds={initiallySelectedProductIds}
                />
                <Button onClick={handleConfirm} disabled={areNoProductsSelected}>
                    {t("products.RelatedProductsAddDialog.confirm")}
                </Button>
            </Box>
        </BasicDialog>
    );
};

const ProductsTable = (props) => {
    const { t } = useTranslation();
    const { pagination, dataLocal, setDataLocal, initiallySelectedProductIds = [] } = props;

    const getLocalizedProperty = useLocalizedProperty();

    const [recordsCount, setRecordsCount] = useState(0);

    const { data: types } = useProductTypes(new URLSearchParams(), {
        select: (data) =>
            data.map((type) => ({
                id: type.id,
                label: getLocalizedProperty(type, "name"),
            })),
    });

    const { data: statuses } = useProductStatuses(new URLSearchParams(), {
        select: (data) =>
            data.results.map((status) => ({
                value: status.status_name_eng,
                label: t(`products.status.${status.status_name_eng}`),
            })),
    });

    const { data: productRows } = useAdjacentProducts(pagination.searchParams);

    useEffect(() => {
        if (productRows?.results) {
            const products = props.excludedProductId
                ? productRows.results.filter((product) => product.id !== props.excludedProductId)
                : productRows.results;
            const productsWithSelection = products.map((product) => ({
                ...product,
                selected: initiallySelectedProductIds.includes(product.id),
            }));
            setDataLocal(productsWithSelection);
            setRecordsCount(productRows.count);
        }
    }, [productRows, setDataLocal, props.excludedProductId, setRecordsCount]);

    const toggleSelect = useCallback(
        (productId) => {
            setDataLocal(
                dataLocal.map((p) =>
                    // @ts-ignore
                    p.id === productId ? { ...p, selected: !p.selected } : p,
                ),
            );
        },
        [dataLocal, setDataLocal],
    );

    return (
        <Box>
            <FilterTableWrapper
                data={dataLocal}
                countRecords={recordsCount}
                localPagination={pagination}
                filtersOpts={{ showCleanFilterIcon: true }}
                onSearchFiltersChange={pagination.handleChangeSearchParams}
                fieldsConfig={[
                    {
                        getValueCallback: (row) => {
                            return (
                                <Checkbox
                                    onChange={() => toggleSelect(row.id)}
                                    // @ts-ignore
                                    checked={row.selected || false}
                                />
                            );
                        },
                    },
                    {
                        name: "product_number",
                        getValueCallback: (rowData) => (
                            <Grid
                                item
                                sx={{
                                    minHeight: 40,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {rowData.product_number}
                            </Grid>
                        ),
                        label: t("products.columns.salesNumber"),
                        filterType: TEXT_FIELD_TYPE,
                        // @ts-ignore
                        getFilterFieldConfigCallback: (filteringData) => undefined,
                        sortable: false,
                        minWidth: 120,
                        width: 120,
                    },
                    {
                        name: "type_id",
                        other_export_field_name: "type",
                        getValueCallback: (rowData) =>
                            rowData.type ? getLocalizedProperty(rowData.type, "name") : "",
                        label: t("products.columns.productType"),
                        filterType: AUTOCOMPLETE_FIELD_TYPE,
                        minWidth: 120,
                        width: 120,
                        fieldConfig: {
                            optionLabelKey: "label",
                            options: types || [],
                            multiple: true,
                            isObjectOption: true,
                            disableClearable: false
                        },
                    },
                    {
                        name: "floor",
                        getValueCallback: (rowData) => rowData.floor,
                        label: t("products.columns.floor"),
                        filterType: NUMERIC_RANGE_FIELD_TYPE,
                        // @ts-ignore
                        getFilterFieldConfigCallback: (filteringData) => undefined,
                        sortable: false,
                        minWidth: 120,
                        width: 120,
                    },
                    {
                        name: "status",
                        getValueCallback: (rowData) => (
                            <ProductStatusText status={rowData.status} />
                        ),
                        label: t("products.columns.productStatus"),
                        filterType: SELECT_FIELD_TYPE,
                        // @ts-ignore
                        getFilterFieldConfigCallback: (filteringData) => ({
                            optionKey: "value",
                            optionLabel: "label",
                            options: statuses,
                        }),
                        sortable: false,
                        minWidth: 120,
                        width: 120,
                        fieldConfig: {
                            optionKey: "value",
                            optionLabel: "label",
                            options: statuses || [],
                        },
                    },
                ]}
            />
        </Box>
    );
};
